import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import DOMPurify from 'dompurify';
import {
    ArrowLeft, Edit3, Lock, Unlock, Send, CheckCircle,
    XCircle, Clock, MessageSquare, Download,
    History, Loader, AlertTriangle, User, Trash2,
    Shield, Info, Crown, RotateCcw, X, FileText
} from 'lucide-react';
import CustomSelect from '../../components/ui/CustomSelect';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { useTranslation } from 'react-i18next';

const STATUS_CONFIG = {
    draft: { key: 'docs.status_draft', cls: 'text-muted bg-surface-2 border-border' },
    published: { key: 'docs.status_published', cls: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800' },
    pending_approval: { key: 'docs.status_pending_approval', cls: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800' },
    approved: { key: 'docs.status_approved', cls: 'text-green-600 bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' },
    rejected: { key: 'docs.status_rejected', cls: 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' },
    locked: { key: 'docs.status_locked', cls: 'text-purple-600 bg-purple-50 border-purple-200 dark:bg-purple-900/20 dark:border-purple-800' },
};

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function getTranslatedSummary(summary, t) {
    if (!summary) return t('docs.initial_version', 'Boshlang\'ich versiya');
    if (summary.includes('Initial') || summary.includes('Boshlang\'ich') || summary.includes('yaratildi')) {
        return t('docs.initial_version', 'Boshlang\'ich versiya');
    }
    if (summary.includes('PDF fayl') || summary.includes('PDF file') || summary.includes('almashtirildi')) {
        return t('docs.pdf_replaced', 'PDF fayl almashtirildi');
    }
    return summary;
}

function ApprovalStatusIcon({ status }) {
    if (status === 'approved') return <CheckCircle className="w-4 h-4 text-green-500" />;
    if (status === 'rejected') return <XCircle className="w-4 h-4 text-red-500" />;
    return <Clock className="w-4 h-4 text-amber-500" />;
}

function CommentItem({ comment, currentUser, onReply, onDelete, rootId = null, canDeleteAny = false, quotedComment = null, canDeleteOwn = true }) {
    const { t } = useTranslation();
    const [showReplies, setShowReplies] = useState(false);
    const [showReplyInput, setShowReplyInput] = useState(false);
    const [replyText, setReplyText] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const effectiveRootId = rootId || comment.id;
    const isReply = !!rootId;

    const [repliedToComment, setRepliedToComment] = useState(null);

    const isOwn = String(comment.user_id) === String(currentUser?.id);
    const showDelete = (isOwn && canDeleteOwn) || canDeleteAny;

    function openReply() {
        setReplyText('');
        setRepliedToComment(comment);
        setShowReplyInput(true);
        if (!isReply) setShowReplies(true);
    }

    function closeReply() {
        setShowReplyInput(false);
        setReplyText('');
        setRepliedToComment(null);
    }

    async function submitReply() {
        if (!replyText.trim()) return;
        setSubmitting(true);
        await onReply(effectiveRootId, replyText, comment.id);
        setReplyText('');
        setRepliedToComment(null);
        setShowReplyInput(false);
        setSubmitting(false);
    }

    const repliesCount = comment.replies?.length || 0;

    function scrollToQuoted(quotedId) {
        const bubble = document.getElementById(`bubble-${quotedId}`);
        if (!bubble) return;
        bubble.scrollIntoView({ behavior: 'smooth', block: 'center' });
        bubble.style.transition = 'background 0.15s ease';
        bubble.style.background = 'var(--color-brand-alpha, rgba(99,102,241,0.18))';
        setTimeout(() => {
            bubble.style.background = '';
            setTimeout(() => { bubble.style.transition = ''; }, 300);
        }, 800);
    }

    return (
        <div id={`comment-${comment.id}`} className="group">
            <div className="flex gap-3">
                <div className={`${isReply ? 'w-7 h-7' : 'w-8 h-8'} rounded-full bg-brand/10 text-brand flex items-center justify-center font-bold flex-shrink-0`}
                    style={{ fontSize: isReply ? '11px' : '13px' }}>
                    {comment.user?.last_name_ru?.charAt(0) || <User className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div id={`bubble-${comment.id}`} className={`inline-block rounded-2xl rounded-tl-sm px-3.5 py-2.5 max-w-full ${
                        comment.is_deleted ? 'bg-surface-2/50 border border-border/50' : 'bg-surface-2'
                    }`}>
                        {comment.is_deleted ? (
                            <span className="flex items-center gap-1.5 text-sm text-muted italic">
                                <Trash2 className="w-3.5 h-3.5 opacity-50" />
                                {t('docs.deleted_comment', "Xabar o'chirilgan")}
                            </span>
                        ) : (
                            <>
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="font-semibold text-xs text-brand block leading-tight">
                                        {comment.user ? `${comment.user.last_name_ru} ${comment.user.first_name_ru?.charAt(0)}.` : t('docs.history_user', 'Foydalanuvchi')}
                                    </span>
                                    {comment.page_number && (
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-500/10 text-blue-500 border border-blue-500/20">
                                            {t('docs.comment_page_badge', 'Bet')}: {comment.page_number}
                                        </span>
                                    )}
                                    {comment.paragraph_number && (
                                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20">
                                            {t('docs.comment_paragraph_badge', 'Punkt')}: {comment.paragraph_number}
                                        </span>
                                    )}
                                </div>

                                {isReply && quotedComment && (
                                    <div
                                        onClick={() => !quotedComment.replied_to_is_deleted && !quotedComment.is_deleted && scrollToQuoted(quotedComment.id)}
                                        className={`mt-1.5 mb-1.5 pl-2.5 border-l-2 border-brand/50 bg-brand/5 rounded-r-lg py-1 pr-2 transition-colors ${
                                            (!quotedComment.replied_to_is_deleted && !quotedComment.is_deleted)
                                                ? 'cursor-pointer hover:bg-brand/10 hover:border-brand active:bg-brand/20'
                                                : ''
                                        }`}
                                    >
                                        <span className="font-semibold text-[11px] text-brand block leading-none mb-0.5">
                                            {quotedComment.replied_to_user_name || t('docs.history_user', 'Foydalanuvchi')}
                                        </span>
                                        <span className="text-xs text-muted line-clamp-1 italic">
                                            {quotedComment.replied_to_is_deleted ? t('docs.deleted_comment', "Xabar o'chirilgan") : quotedComment.replied_to_content}
                                        </span>
                                    </div>
                                )}

                                <p className="text-sm text-primary whitespace-pre-wrap leading-relaxed break-words">{comment.content}</p>
                            </>
                        )}
                    </div>

                    {!comment.is_deleted && (
                        <div className="flex items-center gap-3 mt-1 ml-1 text-xs text-muted">
                            <span>{formatDate(comment.created_at)}</span>
                            {canDeleteOwn && (
                                <button onClick={openReply} className="hover:text-brand transition-colors font-medium">
                                    {t('docs.reply', 'Javob berish')}
                                </button>
                            )}
                            {showDelete && (
                                <button onClick={() => onDelete(comment.id)} className="hover:text-red-500 transition-colors">
                                    {t('docs.delete_comment', "O'chirish")}
                                </button>
                            )}
                        </div>
                    )}

                    {showReplyInput && (
                        <div className="mt-2 pl-3 border-l-2 border-brand/30 space-y-2">
                            {repliedToComment && (
                                <div className="flex items-center justify-between bg-surface-2 px-2.5 py-1.5 rounded-lg text-xs">
                                    <span className="text-muted truncate">
                                        <span className="text-brand font-semibold">
                                            {repliedToComment.user ? `${repliedToComment.user.last_name_ru}` : ''}:
                                        </span>{' '}
                                        {repliedToComment.content}
                                    </span>
                                    <button onClick={closeReply} className="text-muted hover:text-primary ml-2 flex-shrink-0">
                                        <X className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            )}
                            <div className="flex gap-2 items-center">
                                <input
                                    value={replyText}
                                    onChange={e => setReplyText(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && submitReply()}
                                    placeholder={t('docs.write_reply', 'Javobingizni yozing...')}
                                    autoFocus
                                    className="flex-1 px-3 py-1.5 rounded-xl border border-border bg-surface text-xs focus:outline-none focus:ring-2 focus:ring-brand/30"
                                />
                                <button
                                    onClick={submitReply}
                                    disabled={submitting || !replyText.trim()}
                                    className="px-3 py-1.5 rounded-xl bg-brand text-inverse text-xs font-semibold hover:bg-brand/90 disabled:opacity-50 transition-colors flex-shrink-0"
                                >
                                    {submitting ? <Loader className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                </button>
                            </div>
                        </div>
                    )}

                    {!isReply && repliesCount > 0 && (
                        <div className="mt-2">
                            <button
                                onClick={() => setShowReplies(!showReplies)}
                                className="flex items-center gap-1.5 text-xs text-brand font-semibold hover:underline"
                            >
                                <MessageSquare className="w-3.5 h-3.5" />
                                {showReplies
                                    ? t('docs.hide_replies', 'Javoblarni yashirish')
                                    : `${repliesCount} ${t('docs.show_replies', 'ta javobni ko\'rish')}`
                                }
                            </button>
                            {showReplies && (
                                <div className="mt-3 pl-4 border-l-2 border-border space-y-3">
                                    {comment.replies.map(r => (
                                        <CommentItem
                                            key={r.id}
                                            comment={r}
                                            currentUser={currentUser}
                                            onReply={onReply}
                                            onDelete={onDelete}
                                            rootId={comment.id}
                                            canDeleteAny={canDeleteAny}
                                            quotedComment={r}
                                            canDeleteOwn={canDeleteOwn}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function DocViewPage({ docId = null, embedded = false, onNavigate = null }) {
    const { t, i18n } = useTranslation();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const id = docId || params.id;
    const initialVersion = searchParams.get('version') ? Number(searchParams.get('version')) : null;
    const navigate = useNavigate();
    const location = useLocation();
    const basePath = embedded ? null : (location.pathname.startsWith('/admin') ? '/admin/docs' : '/panel/docs');
    const { user } = useAuth();

    const handleNavigate = (path) => {
        if (embedded && onNavigate) {
            if (path === 'docs_list') onNavigate('docs_list');
            else if (path.endsWith('_edit')) onNavigate(`doc_${path}`);
            else onNavigate(`doc_${path}`);
        } else if (basePath) {
            if (path === 'docs_list') navigate(basePath);
            else if (path.endsWith('_edit')) navigate(`${basePath}/${path.replace('_edit', '/edit')}`);
            else navigate(`${basePath}/${path}`);
        }
    };

    // State
    const [doc, setDoc] = useState(null);
    const [versions, setVersions] = useState([]);
    const [comments, setComments] = useState([]);
    const [approvals, setApprovals] = useState([]);
    const [approvers, setApprovers] = useState([]);
    const [viewers, setViewers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('content');
    const [newComment, setNewComment] = useState('');
    const [newCommentPage, setNewCommentPage] = useState('');
    const [newCommentParagraph, setNewCommentParagraph] = useState('');
    const [commentError, setCommentError] = useState('');
    const [commentLoading, setCommentLoading] = useState(false);
    const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null });
    const [actionLoading, setActionLoading] = useState('');
    const [selectedVersion, setSelectedVersion] = useState(initialVersion);
    const [versionContent, setVersionContent] = useState('');
    const [error, setError] = useState('');
    const [addingApprover, setAddingApprover] = useState(false);
    const [selectedCandidate, setSelectedCandidate] = useState('');
    const [candidates, setCandidates] = useState([]);
    const [timeLeft, setTimeLeft] = useState(null);
    const [approveModal, setApproveModal] = useState(false);
    const [rejectModal, setRejectModal] = useState(false);
    const [approvalModalComment, setApprovalModalComment] = useState('');
    const [descModal, setDescModal] = useState(false);

    // PDF blob URL state
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState('');

    useEffect(() => {
        if (!doc?.comment_deadline_at) { setTimeLeft(null); return; }
        const updateTimer = () => {
            const now = new Date();
            const deadline = new Date(doc.comment_deadline_at);
            const diff = deadline - now;
            setTimeLeft(diff <= 0 ? 0 : diff);
        };
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [doc?.comment_deadline_at]);

    const formatTimeLeft = (ms) => {
        if (ms === null) return '';
        if (ms <= 0) return t('docs.deadline_expired', 'Muddati tugagan');
        const days    = Math.floor(ms / (1000 * 60 * 60 * 24));
        const hours   = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((ms % (1000 * 60)) / 1000);

        if (days > 0)    return t('docs.time_days_left',    '{{days}} kun {{hours}} soat qoldi',       { days, hours });
        if (hours > 0)   return t('docs.time_hours_left',   '{{hours}} soat {{minutes}} daqiqa qoldi', { hours, minutes });
        if (minutes > 0) return t('docs.time_minutes_left', '{{minutes}} daqiqa qoldi',                { minutes });
        return              t('docs.time_seconds_left',  '{{seconds}} sekund qoldi',             { seconds });
    };

    const isAdminPage = location.pathname.startsWith('/admin');
    const isAssigned = !!(doc?.created_by_id === user?.id || approvers.some(a => a.user_id === user?.id) || viewers.some(v => v.user_id === user?.id));
    const canEdit = isAssigned && !!(user?.is_superadmin || user?.can_edit_documents);
    // Faqat admin panelda hujjat yaratgan odam (egasi) yoki superadmin tasdiqlovchi qo'sha oladi
    const canManageApprovers = isAdminPage && (user?.is_superadmin || String(doc?.created_by_id) === String(user?.id));
    const canUnlock = !!(user?.is_superadmin || user?.can_unlock_documents);
    const canExport = user?.is_superadmin || user?.can_export_documents || isAssigned;
    const isMyApprover = approvers.some(a => a.user_id === user?.id);
    const canApprove = isMyApprover;
    const canDeleteAnyComment = !!(user?.is_superadmin || user?.can_delete_any_comment);

    async function fetchAll() {
        setLoading(true);
        setError('');
        try {
            const [docRes, verRes, comRes, appRes, approverRes, viewerRes] = await Promise.all([
                api.get(`/docs/${id}`),
                api.get(`/docs/${id}/versions`),
                api.get(`/docs/${id}/comments`),
                api.get(`/docs/${id}/approvals`),
                api.get(`/docs/${id}/approvers`),
                api.get(`/docs/${id}/viewers`),
            ]);
            setDoc(docRes.data);
            setVersions(verRes.data);
            setComments(comRes.data);
            setApprovals(appRes.data);
            setApprovers(approverRes.data);
            setViewers(viewerRes.data);
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 403) setError('__forbidden__');
            else setError(detail || e.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { if (id) fetchAll(); }, [id]);

    useEffect(() => {
        if (canManageApprovers) {
            api.get('/docs/approver-candidates').then(r => setCandidates(r.data)).catch(() => {});
        }
    }, [canManageApprovers]);

    // Fetch PDF blob URL when on content tab
    useEffect(() => {
        if (!doc?.pdf_filename || activeTab !== 'content') return;
        setPdfLoading(true);
        setPdfError('');
        const endpoint = selectedVersion
            ? `/docs/${id}/pdf?version=${selectedVersion}`
            : `/docs/${id}/pdf`;
        api.get(endpoint, { responseType: 'blob' })
            .then(res => {
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                setPdfBlobUrl(url);
            })
            .catch(e => {
                setPdfError(e?.response?.data?.detail || e.message || 'PDF yuklab bo\'lmadi');
            })
            .finally(() => setPdfLoading(false));
        return () => {
            setPdfBlobUrl(prev => { if (prev && prev.startsWith('blob:')) window.URL.revokeObjectURL(prev); return null; });
        };
    }, [doc?.pdf_filename, doc?.current_version, activeTab, id, selectedVersion]);

    async function handleAddApprover() {
        if (!selectedCandidate) return;
        setAddingApprover(true);
        try {
            await api.post(`/docs/${id}/approvers`, { user_id: selectedCandidate, order_index: approvers.length });
            setSelectedCandidate('');
            const { data } = await api.get(`/docs/${id}/approvers`);
            setApprovers(data);
        } catch (e) { setError(e.response?.data?.detail || e.message); }
        finally { setAddingApprover(false); }
    }

    function handleRemoveApprover(approverId) {
        const ap = approvers.find(a => a.id === approverId);
        if (ap && String(ap.user_id) === String(doc?.created_by_id)) {
            setError(t('docs.cannot_remove_owner', "Hujjat yaratuvchisini ruxsatlardan olib tashlab bo'lmaydi!"));
            return;
        }
        setConfirmModal({
            open: true,
            title: t('docs.remove_approver', 'Tasdiqlovchini olib tashlash'),
            message: t('docs.remove_approver_confirm', 'Ushbu tasdiqlovchini olib tashlamoqchimisiz?'),
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, open: false }));
                try {
                    await api.delete(`/docs/${id}/approvers/${approverId}`);
                    setApprovers(prev => prev.filter(a => a.id !== approverId));
                } catch (e) { setError(e.response?.data?.detail || e.message); }
            }
        });
    }

    async function handleRevokeApproval() {
        setConfirmModal({
            open: true,
            title: t('docs.revoke_approval_title', 'Tasdiqlashni qaytarib olish'),
            message: t('docs.revoke_approval_confirm', 'Hujjat uchun tasdiqlashingizni qaytarib olmoqchimisiz? Hujjat qayta tasdiqlash holatiga o\'tadi.'),
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, open: false }));
                try {
                    await api.post(`/docs/${id}/revoke-approval`);
                    fetchAll();
                } catch (e) { setError(e.response?.data?.detail || e.message); }
            }
        });
    }

    async function handleComment() {
        if (!newComment.trim() || !newCommentPage || !newCommentParagraph.trim()) {
            setCommentError(t('docs.comment_fields_required', 'Sahifa, punkt va izoh matni to\'ldirilishi shart'));
            return;
        }
        setCommentError('');
        setCommentLoading(true);
        try {
            await api.post(`/docs/${id}/comments`, {
                content: newComment.trim(),
                page_number: parseInt(newCommentPage, 10),
                paragraph_number: newCommentParagraph.trim(),
            });
            setNewComment('');
            setNewCommentPage('');
            setNewCommentParagraph('');
            const { data } = await api.get(`/docs/${id}/comments`);
            setComments(data);
        } catch (e) { setError(e.message); }
        finally { setCommentLoading(false); }
    }

    async function handleReply(parentId, text, repliedToId = null) {
        try {
            await api.post(`/docs/${id}/comments`, {
                content: text,
                parent_id: parentId,
                replied_to_id: repliedToId || undefined,
            });
            const { data } = await api.get(`/docs/${id}/comments`);
            setComments(data);
        } catch (e) { setError(e?.response?.data?.detail || e.message || t('docs.reply_error', 'Javob yuborishda xatolik')); }
    }

    async function handleDeleteComment(commentId) {
        try {
            await api.delete(`/docs/${id}/comments/${commentId}`);
            const { data } = await api.get(`/docs/${id}/comments`);
            setComments(data);
        } catch (e) { setError(e?.response?.data?.detail || e.message || t('docs.delete_comment_error', "Izohni o'chirishda xatolik")); }
    }

    function handleUnlock() {
        setConfirmModal({
            open: true,
            title: t('docs.unlock_doc', 'Hujjatni qulfdan chiqarish'),
            message: t('docs.unlock_doc_confirm', 'Hujjatni qulfdan chiqarasizmi?'),
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, open: false }));
                setActionLoading('unlock');
                try {
                    await api.post(`/docs/${id}/unlock`);
                    await fetchAll();
                } catch (e) { setError(e.message); }
                finally { setActionLoading(''); }
            }
        });
    }

    function handleExportPdf() {
        const token = localStorage.getItem('access_token');
        const vParam = selectedVersion ? `&version=${selectedVersion}` : '';
        // location.href: server Content-Disposition:attachment bo'lganda sahifadan ketmaydi, faqat yuklab oladi
        window.location.href = `/api/docs/${id}/pdf?download=true&token=${token}${vParam}`;
    }


    async function loadVersion(vNum) {
        if (vNum === null) { setSelectedVersion(null); setVersionContent(''); return; }
        const { data } = await api.get(`/docs/${id}/versions/${vNum}`);
        setSelectedVersion(vNum);
        setVersionContent(data.content_html || '');
    }

    if (loading) return (
        <div className="flex items-center justify-center h-64 text-muted">
            <Loader className="w-6 h-6 animate-spin mr-2" /> {t('docs.loading', 'Yuklanmoqda...')}
        </div>
    );
    if (error && !doc) return (
        <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted">
            <AlertTriangle className="w-10 h-10 text-red-400" />
            <p>{error === '__forbidden__'
                ? t('docs.no_access', 'Bu hujjatga kirish ruxsati yo\'q. Hujjat egasi sizni qo\'shishi kerak.')
                : error
            }</p>
        </div>
    );

    const cfg = STATUS_CONFIG[doc?.status] || STATUS_CONFIG.draft;
    const displayContent = selectedVersion ? versionContent : (doc?.content_html || '');
    const myDecision = approvals.find(a => a.user_id === user?.id);
    const myPendingApproval = (!myDecision || myDecision.status === 'pending') && (doc?.status === 'published' || doc?.status === 'pending_approval');
    const myApprovedDecision = myDecision && (myDecision.status === 'approved' || myDecision.status === 'rejected');
    const canRevoke = canApprove && myApprovedDecision && !doc?.is_locked;
    const versionChanged = myDecision?.status === 'approved' && myDecision?.approved_at_version != null && doc?.current_version > myDecision.approved_at_version;

    return (
        <div className={`space-y-5 max-w-[1500px] w-full mx-auto ${embedded ? 'p-6' : ''}`}>
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                    {!embedded && (
                        <button onClick={() => handleNavigate('docs_list')} className="p-2 rounded-xl border border-border hover:bg-surface-2 transition-colors text-muted mt-0.5">
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                    )}
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="text-xl font-bold">{doc?.title}</h1>
                            {doc?.description && (
                                <button onClick={() => setDescModal(true)} className="text-brand hover:text-brand-hover p-1 rounded-full hover:bg-brand/10 transition-colors" title={t('docs.short_desc', 'Qisqacha tavsif')}>
                                    <Info className="w-5 h-5" />
                                </button>
                            )}
                            {doc?.is_locked && <Lock className="w-4 h-4 text-purple-500" />}
                        </div>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.cls}`}>
                                {t(cfg.key)}
                            </span>
                            <span className="text-xs text-muted font-mono">v{doc?.current_version}</span>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-wrap">
                    {canExport && (
                        <button onClick={handleExportPdf} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-surface hover:bg-surface-2 text-xs font-medium transition-colors">
                            <Download className="w-3.5 h-3.5" /> PDF
                        </button>
                    )}
                    {isAdminPage && canEdit && !doc?.is_locked && (
                        <button onClick={() => handleNavigate(`${id}_edit`)} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-border bg-surface hover:bg-surface-2 text-xs font-medium transition-colors">
                            <Edit3 className="w-3.5 h-3.5" /> {t('docs.edit', 'Tahrirlash')}
                        </button>
                    )}
                    {versionChanged && (
                        <span className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] font-bold">
                            <AlertTriangle className="w-3 h-3" /> {t('docs.new_version_badge', 'Yangi versiya!')}
                        </span>
                    )}
                    {canUnlock && doc?.is_locked && (
                        <button onClick={handleUnlock} disabled={actionLoading === 'unlock'} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-purple-300 text-purple-600 bg-purple-50 text-xs font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 dark:bg-purple-900/20 dark:border-purple-800">
                            {actionLoading === 'unlock' ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
                            {t('docs.unlock', 'Qulfni ochish')}
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm dark:bg-red-900/20 dark:border-red-800 dark:text-red-400">
                    {error}
                </div>
            )}

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-border">
                {[
                    { key: 'content', label: t('docs.document', 'Hujjat') },
                    { key: 'comments', label: `${t('docs.comments', 'Izohlar')} (${comments.reduce((acc, c) => acc + 1 + (c.replies?.length || 0), 0)})` },
                    { key: 'approvals', label: `${t('docs.approvals', 'Tasdiqlash')} (${approvers.length})` },
                ].map(tab => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                        className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${activeTab === tab.key ? 'border-brand text-brand' : 'border-transparent text-muted hover:text-primary'}`}>
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content tab */}
            {activeTab === 'content' && (
                <div className="space-y-3">
                    {versions.length > 0 && (
                        <div className="flex items-center justify-between gap-4 p-3 rounded-xl bg-surface border border-border flex-wrap shadow-sm">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-muted uppercase tracking-wider">{t('docs.version', 'Versiya')}:</span>
                                <select
                                    value={selectedVersion || ''}
                                    onChange={e => loadVersion(e.target.value ? Number(e.target.value) : null)}
                                    className="px-3 py-1.5 rounded-lg border border-border bg-surface-2 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand/30"
                                >
                                    <option value="">v{doc?.current_version} ({t('docs.current_version', 'Joriy versiya')})</option>
                                    {versions.map(v => (
                                        <option key={v.version_number} value={v.version_number}>
                                            v{v.version_number} — {new Date(v.created_at).toLocaleDateString(i18n.language === 'uz' ? 'uz-UZ' : 'ru-RU')} · {getTranslatedSummary(v.change_summary, t)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {selectedVersion && (
                                <div className="flex items-center gap-3">
                                    <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                        <AlertTriangle className="w-3.5 h-3.5" /> v{selectedVersion} {t('docs.viewing', "ko'rilmoqda")}
                                    </span>
                                    <button
                                        onClick={() => loadVersion(null)}
                                        className="px-2.5 py-1 rounded-lg bg-brand/10 text-brand hover:bg-brand/20 text-xs font-bold transition-colors"
                                    >
                                        {t('docs.back_to_current', 'Joriy versiyaga qaytish')}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {doc?.pdf_filename ? (
                        <div className="bg-surface border border-border rounded-2xl overflow-hidden" style={{ height: 'calc(100vh - 180px)', minHeight: '750px' }}>
                            {pdfLoading && (
                                <div className="w-full h-full flex items-center justify-center text-muted">
                                    <Loader className="w-6 h-6 animate-spin mr-2" />
                                    {t('docs.pdf_loading', 'PDF yuklanmoqda...')}
                                </div>
                            )}
                            {pdfError && (
                                <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-red-500">
                                    <AlertTriangle className="w-8 h-8" />
                                    <p className="text-sm">{pdfError}</p>
                                </div>
                            )}
                            {pdfBlobUrl && !pdfLoading && (
                                <iframe
                                    src={`${pdfBlobUrl}#toolbar=0&navpanes=0`}
                                    className="w-full h-full"
                                    title={doc?.title}
                                    style={{ border: 'none' }}
                                />
                            )}
                        </div>
                    ) : (
                        <div className="bg-surface border border-border rounded-2xl p-8 min-h-[400px]">
                            {displayContent ? (
                                <div
                                    className="doc-view-content"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(displayContent) }}
                                />
                            ) : (
                                <div className="flex flex-col items-center gap-3 text-muted py-12 justify-center">
                                    <FileText className="w-12 h-12 opacity-30" />
                                    <p className="text-sm">{t('docs.no_pdf', 'PDF fayl yuklanmagan')}</p>
                                    {canEdit && !doc?.is_locked && (
                                        <button
                                            onClick={() => navigate(`${basePath}/${id}/edit`)}
                                            className="mt-2 flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand text-brand-contrast text-sm font-semibold hover:bg-brand/90 transition-colors"
                                        >
                                            <Edit3 className="w-4 h-4" />
                                            {t('docs.upload_new_pdf', 'PDF yuklash')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Comments tab */}
            {activeTab === 'comments' && (() => {
                const isOpen = doc?.is_commenting_open;
                const deadline = doc?.comment_deadline_at ? new Date(doc.comment_deadline_at) : null;

                return (
                    <div className="space-y-4">
                        {doc?.status !== 'draft' && deadline && (
                            <div className={`flex items-center justify-between gap-4 px-4 py-3 rounded-xl border text-sm ${isOpen
                                ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                                : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
                                <div className="flex items-center gap-2 text-primary">
                                    <Clock className={`w-4 h-4 ${isOpen ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`} />
                                    <span className="font-semibold">
                                        {isOpen ? t('docs.comment_deadline', 'Izoh qoldirish muddati:') : t('docs.deadline_passed', 'Izoh yozish muddati tugagan')}
                                    </span>
                                </div>
                                <div className={`font-mono text-sm px-3 py-1 rounded-md font-bold ${isOpen ? 'bg-blue-600/10 text-blue-700 dark:text-blue-400' : 'bg-red-600/10 text-red-700 dark:text-red-400'}`}>
                                    {formatTimeLeft(timeLeft)}
                                </div>
                            </div>
                        )}

                        <div className="bg-surface border border-border rounded-2xl p-5 space-y-4 shadow-sm">
                            <h3 className="text-xs font-bold text-muted uppercase tracking-wider flex items-center gap-2">
                                <MessageSquare className="w-4 h-4 text-brand" />
                                {t('docs.add_comment', t('docs.write_comment', 'Izoh yozish...'))}
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[11px] font-bold text-muted mb-1 uppercase tracking-wider">
                                        {t('docs.comment_page', 'Sahifa raqami')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={newCommentPage}
                                        onChange={e => setNewCommentPage(e.target.value)}
                                        placeholder="1"
                                        disabled={isOpen === false || !isAssigned}
                                        className="w-full px-3.5 py-2 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 font-medium"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-muted mb-1 uppercase tracking-wider">
                                        {t('docs.comment_paragraph', 'Punkt raqami')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={newCommentParagraph}
                                        onChange={e => setNewCommentParagraph(e.target.value)}
                                        placeholder="1.2.3"
                                        disabled={isOpen === false || !isAssigned}
                                        className="w-full px-3.5 py-2 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 font-medium"
                                    />
                                </div>
                            </div>

                            {commentError && (
                                <p className="text-xs font-semibold text-red-500 flex items-center gap-1">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    {commentError}
                                </p>
                            )}

                            <div className="flex gap-3 pt-1">
                                <div className="w-8 h-8 rounded-full bg-brand/10 text-brand flex items-center justify-center font-bold text-sm flex-shrink-0 mt-1">
                                    {user?.last_name_ru?.charAt(0) || <User className="w-4 h-4" />}
                                </div>
                                <div className="flex-1 flex gap-2 items-center">
                                    <input
                                        value={newComment}
                                        onChange={e => setNewComment(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleComment()}
                                        placeholder={isOpen === false
                                            ? t('docs.deadline_passed', 'Izoh yozish muddati tugagan')
                                            : (!isAssigned)
                                                ? t('docs.no_comment_permission', 'Izoh qoldirish huquqi yo\'q')
                                                : t('docs.write_comment_placeholder', 'Izoh matnini yozing...')}
                                        disabled={isOpen === false || !isAssigned}
                                        className="flex-1 min-w-0 px-4 py-2.5 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 disabled:opacity-50"
                                    />
                                    <button
                                        onClick={handleComment}
                                        disabled={commentLoading || !newComment.trim() || !newCommentPage || !newCommentParagraph.trim() || isOpen === false || !isAssigned}
                                        className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-brand text-inverse hover:bg-brand/90 disabled:opacity-50 transition-colors shadow-sm"
                                        title={t('docs.send', 'Yuborish')}
                                    >
                                        {commentLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {comments.length === 0 ? (
                                <p className="text-muted text-sm text-center py-8">{t('docs.no_comments', 'Hali izoh yo\'q')}</p>
                            ) : comments.map(c => (
                                <CommentItem key={c.id} comment={c} currentUser={user} onReply={isOpen ? handleReply : () => {}} onDelete={handleDeleteComment} canDeleteAny={canDeleteAnyComment} canDeleteOwn={!!isOpen} />
                            ))}
                        </div>
                    </div>
                );
            })()}

            {/* Approvals tab */}
            {activeTab === 'approvals' && (() => {
                async function doApprove(status) {
                    try {
                        await api.post(`/docs/${id}/approve`, { status, comment: approvalModalComment });
                        setApproveModal(false);
                        setRejectModal(false);
                        setApprovalModalComment('');
                        fetchAll();
                    } catch (e) { setError(e?.response?.data?.detail || e.message); }
                }

                return (
                    <div className="space-y-4">
                        <div className="bg-surface border border-border rounded-2xl p-5 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Shield className="w-5 h-5 text-brand" />
                                    <h3 className="font-bold text-sm">{t('docs.approvers', 'Tasdiqlovchilar')}</h3>
                                    <span className="text-xs text-muted bg-surface-2 px-2 py-0.5 rounded-full">{approvers.length}</span>
                                </div>
                            </div>

                            {/* Approver qo'shish qismi */}
                            {canManageApprovers && candidates.length > 0 && (
                                <div className="flex items-center gap-2 pt-2 border-t border-border/60">
                                    <select
                                        value={selectedCandidate}
                                        onChange={e => setSelectedCandidate(e.target.value)}
                                        className="flex-1 px-3 py-2 rounded-xl border border-border bg-surface-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand/30"
                                    >
                                        <option value="">{t('docs.select_approver_placeholder', 'Tasdiqlovchi tanlang...')}</option>
                                        {candidates.filter(c => !approvers.some(a => a.user_id === c.id)).map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.full_name || `${c.last_name_ru} ${c.first_name_ru}`} {c.is_head_type ? '👑' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        onClick={handleAddApprover}
                                        disabled={addingApprover || !selectedCandidate}
                                        className="px-3 py-2 rounded-xl bg-brand text-inverse text-xs font-semibold hover:bg-brand/90 disabled:opacity-50 transition-colors"
                                    >
                                        {addingApprover ? <Loader className="w-3.5 h-3.5 animate-spin" /> : t('docs.add', 'Qo\'shish')}
                                    </button>
                                </div>
                            )}

                            {approvers.length === 0 ? (
                                <p className="text-muted text-sm py-3 text-center">{t('docs.no_approvers', 'Hali tasdiqlovchi tayinlanmagan')}</p>
                            ) : approvers.map(ap => {
                                const approval = approvals.find(a => a.user_id === ap.user_id);
                                return (
                                    <div key={ap.id} className="flex items-center justify-between px-3 py-2.5 bg-surface-2 rounded-xl">
                                        <div className="flex items-center gap-2.5">
                                            <div className="w-7 h-7 rounded-full bg-brand/10 text-brand flex items-center justify-center text-xs font-bold">
                                                {ap.user?.last_name_ru?.charAt(0) || <User className="w-3.5 h-3.5" />}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-1.5">
                                                    <p className="text-sm font-medium">
                                                        {ap.user ? `${ap.user.last_name_ru} ${ap.user.first_name_ru}` : ap.user_id}
                                                    </p>
                                                    {ap.user?.is_head_type && (
                                                        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 flex-shrink-0" title="Head — tasdiqlash statusi">
                                                            <Crown className="w-3 h-3" />
                                                        </span>
                                                    )}
                                                </div>
                                                {approval?.comment && (
                                                    <p className="text-xs text-muted">{approval.comment}</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {approval ? (
                                                <div className="flex items-center gap-1.5">
                                                    <ApprovalStatusIcon status={approval.status} />
                                                    <span className={`text-xs font-medium ${
                                                        approval.status === 'approved' ? 'text-green-600' :
                                                        approval.status === 'rejected' ? 'text-red-600' : 'text-amber-600'
                                                    }`}>
                                                        {approval.status === 'approved' ? t('docs.approved', 'Tasdiqlandi') :
                                                         approval.status === 'rejected' ? t('docs.rejected', 'Rad etildi') : t('docs.status_pending', 'Kutilmoqda')}
                                                    </span>
                                                    {approval.status === 'approved' && approval.approved_at_version != null && (
                                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                                                            doc?.current_version > approval.approved_at_version
                                                                ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                                                                : 'bg-surface-2 text-muted'
                                                        }`} title={doc?.current_version > approval.approved_at_version ? `v${approval.approved_at_version} da tasdiqlangan, hozir v${doc?.current_version}` : ''}>
                                                            v{approval.approved_at_version}
                                                            {doc?.current_version > approval.approved_at_version && ' ⚠'}
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-xs text-muted">{t('docs.no_decision_yet', 'Hali qaror yo\'q')}</span>
                                            )}
                                            {canManageApprovers && (
                                                <button
                                                    onClick={() => handleRemoveApprover(ap.id)}
                                                    className="p-1 text-muted hover:text-red-500 rounded-lg hover:bg-surface transition-colors"
                                                    title={t('docs.remove', 'O\'chirish')}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {canApprove && myPendingApproval && (
                            <div className="mt-8 p-5 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-500/20 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-4 shadow-sm">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-600 flex items-center justify-center shrink-0">
                                        <CheckCircle className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="text-base font-bold text-foreground">{t('docs.approval_required', 'Sizning qaroringiz kutilyapti')}</h3>
                                        <p className="text-sm text-muted mt-0.5">{t('docs.approval_desc', 'Hujjatni ko\'rib chiqqaningizdan so\'ng qaroringizni bildiring.')}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 w-full sm:w-auto">
                                    <button onClick={() => setRejectModal(true)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl border border-red-500/30 text-red-600 hover:bg-red-500/10 font-bold transition-all">
                                        <XCircle className="w-4 h-4" /> {t('docs.reject', 'Rad etish')}
                                    </button>
                                    <button onClick={() => setApproveModal(true)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white shadow-lg shadow-emerald-500/20 font-bold transition-all">
                                        <CheckCircle className="w-4 h-4" /> {t('docs.approve', 'Tasdiqlash')}
                                    </button>
                                </div>
                            </div>
                        )}

                        {versionChanged && (
                            <div className="mt-6 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-400/30 rounded-2xl flex flex-col sm:flex-row items-start sm:items-center gap-3 shadow-sm">
                                <div className="w-10 h-10 rounded-full bg-amber-500/20 text-amber-600 flex items-center justify-center shrink-0">
                                    <AlertTriangle className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-bold text-amber-700 dark:text-amber-400">{t('docs.version_changed_title', 'Hujjatda o\'zgarishlar aniqlandi')}</h4>
                                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
                                        <span dangerouslySetInnerHTML={{ __html: t('docs.version_changed_desc', 'v{{oldVersion}} da tasdiqlagansiz, hozir v{{newVersion}}.', { oldVersion: myDecision?.approved_at_version, newVersion: doc?.current_version }) }} />
                                    </p>
                                </div>
                                {canRevoke && (
                                    <button onClick={handleRevokeApproval} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-amber-400/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 font-semibold text-sm transition-all shrink-0">
                                        <RotateCcw className="w-4 h-4" /> {t('docs.revoke_approval', 'Qaytarib olish')}
                                    </button>
                                )}
                            </div>
                        )}

                        {canRevoke && !versionChanged && (
                            <div className="mt-6 p-4 bg-gradient-to-r from-blue-500/5 to-indigo-500/5 dark:from-blue-900/10 dark:to-indigo-900/10 border border-blue-500/20 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-500/10 text-blue-600 flex items-center justify-center shrink-0">
                                        <CheckCircle className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-primary">
                                            {myDecision?.status === 'approved' ? t('docs.you_approved', 'Siz bu hujjatni tasdiqlagansiz') : t('docs.you_rejected', 'Siz bu hujjatni rad etgansiz')}
                                            {myDecision?.approved_at_version ? ` (v${myDecision.approved_at_version})` : ''}
                                        </p>
                                        {myDecision?.comment && <p className="text-xs text-muted mt-0.5">{t('docs.comment_label', 'Izoh')}: {myDecision.comment}</p>}
                                    </div>
                                </div>
                                <button onClick={handleRevokeApproval} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-secondary hover:bg-surface-2 font-semibold text-sm transition-all shrink-0">
                                    <RotateCcw className="w-4 h-4" /> {t('docs.revoke_approval', 'Qaytarib olish')}
                                </button>
                            </div>
                        )}

                        {(approveModal || rejectModal) && (
                            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                                <div className="bg-surface border border-border rounded-2xl p-6 shadow-2xl max-w-md w-full mx-4 space-y-4">
                                    <div className="flex items-center gap-3">
                                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                            approveModal ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                                        }`}>
                                            {approveModal
                                                ? <CheckCircle className="w-6 h-6 text-green-500" />
                                                : <XCircle className="w-6 h-6 text-red-500" />}
                                        </div>
                                        <div>
                                            <p className="font-bold">{approveModal ? t('docs.confirm_approve_doc', 'Hujjatni tasdiqlash') : t('docs.confirm_reject_doc', 'Hujjatni rad etish')}</p>
                                            <p className="text-sm text-muted">{t('docs.please_review', 'Iltimos, davom etishdan oldin hujjat bilan tanishib chiqing')}</p>
                                        </div>
                                    </div>

                                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-4 py-3">
                                        <p className="text-sm font-semibold text-amber-700 dark:text-amber-400">
                                            {t('docs.review_warning', '⚠️ Hujjat bilan tanishib chiqdingizmi?')}
                                        </p>
                                        <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                                            {t('docs.decision_irreversible', 'Bu qaror qaytarib bo\'lmaydi. Tasdiqlashdan oldin hujjat mazmunini to\'liq o\'qib chiqing.')}
                                        </p>
                                    </div>

                                    <textarea
                                        value={approvalModalComment}
                                        onChange={e => setApprovalModalComment(e.target.value)}
                                        placeholder={t('docs.comment_optional', 'Izoh (ixtiyoriy)...')}
                                        rows={2}
                                        className="w-full px-3 py-2 rounded-xl border border-border bg-surface-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 resize-none"
                                    />

                                    <div className="flex gap-2 justify-end">
                                        <button
                                            onClick={() => { setApproveModal(false); setRejectModal(false); setApprovalModalComment(''); }}
                                            className="px-4 py-2 text-sm rounded-xl border border-border hover:bg-surface-2 transition-colors"
                                        >
                                            {t('docs.cancel', 'Bekor qilish')}
                                        </button>
                                        <button
                                            onClick={() => doApprove(approveModal ? 'approved' : 'rejected')}
                                            className={`px-5 py-2 text-sm rounded-xl text-white font-semibold transition-colors ${
                                                approveModal ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'
                                            }`}
                                        >
                                            {approveModal ? t('docs.yes_approve', 'Ha, Tasdiqlash') : t('docs.yes_reject', 'Ha, Rad etish')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* History tab */}
            {activeTab === 'history' && (
                <div className="space-y-2">
                    {versions.length === 0 ? (
                        <p className="text-muted text-sm text-center py-8">{t('docs.no_versions', 'Versiyalar yo\'q')}</p>
                    ) : versions.map(v => (
                        <div key={v.id} className="px-4 py-3 bg-surface border border-border rounded-xl hover:bg-surface-2 transition-colors">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-sm font-bold text-brand cursor-default">v{v.version_number}</span>
                                    <p className="text-sm font-medium">{v.title}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    <span className="text-xs text-muted">{formatDate(v.created_at)}</span>
                                    <span className="text-xs text-muted">{v.creator ? `${v.creator.last_name_ru} ${v.creator.first_name_ru?.charAt(0)}.` : ''}</span>
                                    <button onClick={() => { loadVersion(v.version_number); setActiveTab('content'); }}
                                        className="text-xs text-brand hover:underline font-semibold">
                                        {t('docs.view', 'Ko\'rish')}
                                    </button>
                                </div>
                            </div>
                            {v.change_summary && (
                                <div className="flex items-center gap-2 mt-2 ml-[3.25rem]">
                                    <Edit3 className="w-3.5 h-3.5 text-muted flex-shrink-0" />
                                    <p className="text-sm text-secondary leading-snug">{v.change_summary}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <ConfirmModal
                isOpen={confirmModal.open}
                onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
                onConfirm={confirmModal.onConfirm}
                title={confirmModal.title}
                message={confirmModal.message}
                confirmText={t('common.confirm', 'Tasdiqlash')}
                cancelText={t('common.cancel', 'Bekor qilish')}
            />

            {/* Description Modal */}
            <AnimatePresence>
                {descModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                        onClick={() => setDescModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-surface border border-border rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                        >
                            <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface-2/50">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-brand/10 text-brand flex items-center justify-center">
                                        <Info className="w-5 h-5" />
                                    </div>
                                    <h2 className="font-bold text-lg">{t('docs.short_desc', 'Tavsif')}</h2>
                                </div>
                                <button onClick={() => setDescModal(false)} className="p-2 text-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto">
                                <p className="text-primary leading-relaxed whitespace-pre-wrap">{doc?.description}</p>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
